import classnames from "classnames";
import { DefaultButton, Image } from "@fluentui/react";
import { Component, ReactNode } from "react";
import { SmallBody, SmallBodyBolder } from "src/components/Text";
import { localize } from "src/l10n";
import {
    IMarketplaceWidgetContext,
    MarketplaceWidgetContext
} from "../MarketplaceWidgetContext";
import { MarketplaceWidgetData } from "../MarketplaceWidgetData";
import { MarketplaceWidgetErrorBoundary } from "../MarketplaceWidgetErrorBoundary";
import { MarketplaceWidgetLoader } from "../MarketplaceWidgetLoader";
import "./MarketplaceWidget.scss";
import { SpintrTypes } from "src/typings";
import MarketplaceDataLoader from "src/marketplace/utils/data-loader";
import { hasNoContent } from "./widgetContent";

interface IProps {
    data?: Spintr.IMarketplaceWidgetDataResponse;
    id?: string;
    autoWidth?: boolean;
    displayBorder?: boolean;
    /** The title to keep when the data call fails (the failed response has no name). */
    fallbackTitle?: string;
}

interface IState {
    data: Spintr.IMarketplaceWidgetDataResponse;
    /** Bumped by "Försök igen" to load again. */
    loadCount: number;
}

class MarketplaceWidget extends Component<IProps, IState> {
    private readonly widgetContext: IMarketplaceWidgetContext;

    constructor(props: IProps) {
        super(props);

        this.state = {
            data: props.data,
            loadCount: 0,
        };

        this.widgetContext = {
            onDataLoaded: this.onDataLoaded.bind(this),
        };
    }

    public componentDidUpdate(prevProps: IProps): void {
        if (this.props.data && this.props.data !== prevProps.data) {
            this.setState({
                data: this.props.data,
            });
        }
    }

    public render(): ReactNode {
        const { data } = this.state;

        const isLeaderboard = data && !data.failed && data.displayType === SpintrTypes.WidgetType.Leaderboard

        // A single key figure sits 4 px under the title, everything else 8 px
        const isKeyFigure = data &&
            !data.failed &&
            data.displayType === SpintrTypes.WidgetType.KeyPointIndicator &&
            !Array.isArray(data.data);

        // "Kommande händelser (3)" - the count is dropped when there is nothing to show.
        const upcomingEventCount = data &&
            !data.failed &&
            data.displayType === SpintrTypes.WidgetType.UpcomingEvents
                ? (data.data?.events || []).length
                : 0;

        const title = !data
            ? this.props.fallbackTitle
            : upcomingEventCount > 0
                ? data.name + " (" + upcomingEventCount + ")"
                : data.name || this.props.fallbackTitle;

        return (
            <div className={classnames("MarketplaceWidget", {
                autoWidth: this.props.autoWidth,
                displayBorder: !!this.props.displayBorder,
                isLeaderboard: isLeaderboard,
                isKeyFigure: isKeyFigure,
            })}
            aria-busy={!data}>
                <MarketplaceWidgetContext.Provider value={this.widgetContext}>
                    <MarketplaceWidgetErrorBoundary>
                        <div className="information start-page-flexible-padding">
                            {data && data.iconUrl && (
                                <div className="logo">
                                    <Image
                                        height={16}
                                        src={!data ? null : data.iconUrl}
                                        width={16}
                                    />
                                </div>
                            )}
                            <div className="name">
                                {!!title ? (
                                    <SmallBodyBolder as="div" weight="medium" color="contentNormal">
                                        {title}
                                    </SmallBodyBolder>
                                ) : (
                                    <div className="widget-skeleton-title" />
                                )}
                            </div>
                        </div>
                        <div className="data start-page-flexible-padding">
                            {this.props.id && (
                                <MarketplaceWidgetLoader id={this.props.id} key={this.state.loadCount} />
                            )}
                            {!data && (
                                // Blocks where the number and its label go, no text
                                <div className="widget-skeleton" aria-label={localize("Laddar")}>
                                    <div className="widget-skeleton-number" />
                                    <div className="widget-skeleton-label" />
                                </div>
                            )}
                            {data && data.failed && (
                                // Calm, in words and not in red, like Startsidan's other errors
                                <div className="widget-error" role="alert">
                                    <SmallBodyBolder as="div" weight="semiBold" color="contentDark">
                                        {localize((!!data.errorCode && data.errorCode === 404) ? 'NOT_FOUND' : 'TeknisktFel')}
                                    </SmallBodyBolder>
                                    {!!this.props.id && (
                                        <DefaultButton
                                            className="widget-retry"
                                            text={localize("ForsokIgen")}
                                            onClick={this.retry} />
                                    )}
                                </div>
                            )}
                            {data && !data.failed && hasNoContent(data) && (
                                <SmallBody as="div" color="contentNormal" className="widget-empty">
                                    {localize("DetFinnsIngetInnehall")}
                                </SmallBody>
                            )}
                            {data && !data.failed && !hasNoContent(data) && (
                                <MarketplaceWidgetData
                                    config={data.config}
                                    data={data.data}
                                    displayType={data.displayType}
                                    variables={data.variables}
                                />
                            )}
                        </div>
                    </MarketplaceWidgetErrorBoundary>
                </MarketplaceWidgetContext.Provider>
            </div>
        );
    }

    private onDataLoaded(data: Spintr.IMarketplaceWidgetDataResponse): void {
        this.setState({ data });
    }

    // The loader keeps a failed answer for a while; drop it and load once more
    private retry = (): void => {
        MarketplaceDataLoader.remove(this.props.id);

        this.setState((state) => ({
            data: undefined,
            loadCount: state.loadCount + 1,
        }));
    }
}

export default MarketplaceWidget;
